import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import tw from "twin.macro";
import {
	SectionHeading,
	Subheading as SubheadingBase,
} from "components/misc/Headings.js";
import { SectionDescription } from "components/misc/Typography.js";

import defaultCardImage from "images/shield-icon.svg";
import { ReactComponent as SvgDecoratorBlob3 } from "images/svg-decorator-blob-3.svg";

import SupportIconImage from "images/building.svg";
import ShieldIconImage from "images/analytics.svg";
import CustomizeIconImage from "images/location-pin.svg";
import FastIconImage from "images/alert-triangle.svg";
import ReliableIconImage from "images/group.svg";

// Section background with a darker neutral tone to emphasize professionalism
const Container = styled.div`
	${tw`relative bg-gray-100 px-8`}// Light neutral background to keep the focus on the cards
`;

const ThreeColumnContainer = styled.div`
	${tw`flex flex-col items-center md:items-stretch md:flex-row flex-wrap md:justify-center max-w-screen-xl mx-auto py-16 md:py-20`}// Adjusted padding for balance
`;

const Subheading = tw(SubheadingBase)`mb-4 text-blue-600`; // Soft blue for accenting titles
const Heading = tw(SectionHeading)`w-full text-gray-900`; // Darker gray for the main heading
const Description = tw(SectionDescription)`w-full text-center text-gray-700`; // A balanced gray for description

const VerticalSpacer = tw.div`mt-12 w-full`;

const Column = styled.div`
	${tw`md:w-1/2 lg:w-1/3 max-w-lg mb-8`}// Max-width adjusted for better text readability
`;

const Card = styled.div`
	${tw`flex flex-col sm:flex-row items-center sm:items-start text-center sm:text-left h-full md:mx-4 mx-0 p-6 rounded-lg border border-gray-300 bg-white shadow-md`} // Subtle border and shadow for minimalist professional look
  transition: all 0.2s ease-in-out;

	&:hover {
		${tw`shadow-lg`}// Light shadow effect on hover to maintain focus
	}

	.imageContainer {
		${tw`border text-center rounded-full p-4 flex-shrink-0 border-gray-300`} // Border to keep icons visually distinct
    img {
			${tw`w-10 h-10`}// Slightly larger icons for prominence
		}
	}

	.textContainer {
		${tw`sm:ml-4 mt-4 sm:mt-0`}
	}

	.title {
		${tw`text-xl font-semibold text-blue-600`}// Blue accent for titles to fit the theme
	}

	.description {
		${tw`mt-2 text-sm text-gray-600 leading-relaxed`}// Soft gray for description text, smaller size for fitting longer text
	}
`;

const DecoratorBlob = styled(SvgDecoratorBlob3)`
	${tw`pointer-events-none absolute right-0 bottom-0 w-64 opacity-25 transform translate-x-32 translate-y-48 `}
`;

export default ({
	cards = null,
	heading = "Our Services",
	subheading = "Services",
	description = "We offer a range of services to ensure the security and protection of your facilities.",
}) => {
	const { t } = useTranslation();

	// Use useMemo to memoize the card data
	const memoizedCards = useMemo(() => {
		if (cards && cards.length > 0) {
			return cards;
		}
		return [
			{
				imageSrc: ShieldIconImage,
				title: t("services.systemCreationTitle"), // "System Creation"
				description: t("services.systemCreationDescription"), // "Establishing the structure and system for the guard service management."
			},
			{
				imageSrc: SupportIconImage,
				title: t("services.strategicAnalysisTitle"), // "Strategic Analysis"
				description: t("services.strategicAnalysisDescription"), // "Analysis of the existing guard service and proposal of the strategic solutions."
			},
			{
				imageSrc: CustomizeIconImage,
				title: t("services.riskAssessmentTitle"), // "Risk Assessment"
				description: t("services.riskAssessmentDescription"), // "Assessment of the security and vulnerability of the territory location and infrastructure."
			},
			{
				imageSrc: FastIconImage,
				title: t("services.gapIdentificationTitle"), // "Gap Identification"
				description: t("services.gapIdentificationDescription"), // "Identification of gaps, outdated equipment, incomplete and irrelevant policies and procedures in the guard system."
			},
			{
				imageSrc: ReliableIconImage,
				title: t("services.staffConsultingTitle"), // "Staff Consulting"
				description: t("services.staffConsultingDescription"), // "Consulting and organizing the work of the guard staff for the enterprises and facilities effective protection from any unauthorized access."
			},
		];
	}, [cards, t]); // Only re-calculate if `cards` or translation `t` changes

	return (
		<Container>
			<ThreeColumnContainer>
				<Subheading>{t("services.subheading")}</Subheading>
				<Heading>{t("services.heading")}</Heading>
				<Description>{t("services.description")}</Description>
				<VerticalSpacer />
				{memoizedCards.map((card, i) => (
					<Column key={i}>
						<Card>
							<span className="imageContainer">
								<img src={card.imageSrc || defaultCardImage} alt="" />
							</span>
							<span className="textContainer">
								<span className="title">{card.title}</span>
								<p className="description">{card.description}</p>
							</span>
						</Card>
					</Column>
				))}
			</ThreeColumnContainer>
		</Container>
	);
};
